import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import BAULT_ABI from 'abis/Bault.json'
import { REFETCH_POLICY_FAST, RefetchPolicy, TANSTACK_REFETCH_POLICY_SLOW } from 'config/RefetchPolicies'
import { useBaultById } from 'hooks/backend/baults'
import { useToken } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import { useCallback, useMemo } from 'react'
import { Abi, Address } from 'viem'

import { useReadContractMultiple } from './useReadContractMultiple'
import { useTokenValue } from './useTokenPrice'

/**
 * Hook to fetch basic information about a Bault
 */
export const useBaultBasicInfo = (baultAddress: string | undefined) => {
  const { data: baultData, isLoading, error } = useBaultById(baultAddress, TANSTACK_REFETCH_POLICY_SLOW)

  const assetToken = useToken(baultData?.stakingToken.id)

  const shareToken = useToken(baultAddress)

  const data = useMemo(() => {
    if (!baultData) return null

    const exitFeeBps = baultData.exitFeeBps || 0
    const exitFeePercent = exitFeeBps / 100

    return {
      assetToken,
      underlyingToken: assetToken,
      exitFeeBps,
      exitFeePercent,
      rewardVaultAddress: baultData.rewardVault,
      bounty: baultData.bounty?.toString(),
      shareToken,
      name: baultData.name || '',
      symbol: baultData.symbol || '',
      baultAddress,

      apy: baultData.apy,
      sharePrice: baultData.sharePrice,
      compoundCount: baultData.compoundCount,
      compoundFeeBps: baultData.compoundFeeBps,
      totalAssets: baultData.totalAssets,
      totalSupply: baultData.totalSupply,
      createdAt: baultData.createdAt,
      lastUpdatedAt: baultData.lastUpdatedAt,
    }
  }, [baultData, assetToken, shareToken, baultAddress])

  return {
    isLoading,
    error,
    data,
    exists: !!baultData,
  }
}

/**
 * Hook to fetch total supply and TVL information
 */
export const useBaultTVL = (baultAddress?: Address) => {
  const { data: basicInfo } = useBaultBasicInfo(baultAddress)

  const contracts = useMemo(() => {
    if (!baultAddress) return []
    return [
      {
        address: baultAddress,
        abi: BAULT_ABI as Abi,
        functionName: 'totalSupply' as const,
      },
      {
        address: baultAddress,
        abi: BAULT_ABI as Abi,
        functionName: 'totalAssets' as const,
      },
    ]
  }, [baultAddress])

  const {
    data: results,
    isLoading,
    isError,
  } = useReadContractMultiple({
    contracts,
    query: {
      enabled: contracts.length > 0,
    },
  })

  const totalShares = useMemo(() => {
    if (!basicInfo?.shareToken || !results?.[0]?.result) return undefined

    try {
      const totalSupply = results[0].result
      return CurrencyAmount.fromRawAmount(basicInfo.shareToken, totalSupply.toString())
    } catch (e) {
      console.error('Error creating totalShares:', e)
      return undefined
    }
  }, [basicInfo?.shareToken, results])

  const totalAssets = useMemo(() => {
    if (!basicInfo?.underlyingToken || !results?.[1]?.result) return undefined

    try {
      const assets = results[1].result
      return CurrencyAmount.fromRawAmount(basicInfo.underlyingToken, assets.toString())
    } catch (e) {
      console.error('Error creating totalAssets:', e)
      return undefined
    }
  }, [basicInfo?.underlyingToken, results])

  const totalAssetsUSD = useTokenValue(totalAssets)

  return {
    isLoading,
    error: isError,
    totalShares,
    totalAssets,
    value: totalAssetsUSD,
  }
}

/**
 * Hook to fetch user-specific Bault information
 */
export const useBaultUserInfo = (baultAddress?: Address, accountAddress?: Address, refetchPolicy?: RefetchPolicy) => {
  const { account: connectedAccount } = useConnection()
  const account = useMemo(() => accountAddress || connectedAccount, [accountAddress, connectedAccount])
  const { data: basicInfo } = useBaultBasicInfo(baultAddress)

  const contracts = useMemo(() => {
    if (!baultAddress || !account) return []
    return [
      {
        address: baultAddress,
        abi: BAULT_ABI as Abi,
        functionName: 'maxWithdraw' as const,
        args: [account],
      },
      {
        address: baultAddress,
        abi: BAULT_ABI as Abi,
        functionName: 'balanceOf' as const,
        args: [account],
      },
    ]
  }, [baultAddress, account])

  const {
    data: results,
    isLoading,
    isError,
  } = useReadContractMultiple({
    contracts,
    query: {
      ...refetchPolicy,
      enabled: contracts.length > 0,
    },
  })

  const userShares = useMemo(() => {
    if (!basicInfo?.shareToken || !results?.[1]?.result) return undefined

    try {
      const balance = results[1].result
      return CurrencyAmount.fromRawAmount(basicInfo.shareToken, balance.toString())
    } catch (e) {
      console.error('Error creating userShares:', e)
      return undefined
    }
  }, [basicInfo?.shareToken, results])

  const estimatedLpTokens = useMemo(() => {
    if (!basicInfo?.underlyingToken || !results?.[0]?.result) return undefined

    try {
      const assets = results[0].result
      return CurrencyAmount.fromRawAmount(basicInfo.underlyingToken, assets.toString())
    } catch (e) {
      console.error('Error creating estimatedLpTokens:', e)
      return undefined
    }
  }, [basicInfo?.underlyingToken, results])

  const estimatedLpTokensForShares = useCallback(
    (shares: CurrencyAmount<Token>) => {
      if (!shares || !userShares || !estimatedLpTokens || userShares.equalTo('0')) return undefined

      try {
        const proportion = shares.divide(userShares)
        return estimatedLpTokens.multiply(proportion)
      } catch (e) {
        console.error('Error calculating estimatedLpTokensForShares:', e)
        return undefined
      }
    },
    [userShares, estimatedLpTokens]
  )

  const estimatedLpTokensUSD = useTokenValue(estimatedLpTokens)
  const positions = useMemo(() => {
    if (!userShares || userShares.equalTo('0')) return []

    return [
      {
        id: `${baultAddress}-${account}`,
        liquidity: userShares,
        tokenId: `${baultAddress}-position-${account}`,
        estimatedLpTokens,
      },
    ]
  }, [userShares, baultAddress, account, estimatedLpTokens])

  const data = useMemo(() => {
    return {
      userShares,
      estimatedLpTokens,
      estimatedLpTokensUSD,
      positions,
      estimatedLpTokensForShares,
    }
  }, [userShares, estimatedLpTokens, estimatedLpTokensUSD, positions, estimatedLpTokensForShares])

  return {
    isLoading,
    error: isError,
    data,
  }
}

/**
 * Combined hook that provides all Bault data
 */
export const useBaultDatas = (baultAddress?: Address, accountAddress?: Address) => {
  const basicInfo = useBaultBasicInfo(baultAddress)
  const tvlInfo = useBaultTVL(baultAddress)
  const userInfo = useBaultUserInfo(baultAddress, accountAddress, REFETCH_POLICY_FAST)

  const isLoading = useMemo(() => {
    return basicInfo.isLoading || tvlInfo.isLoading || userInfo.isLoading
  }, [basicInfo.isLoading, tvlInfo.isLoading, userInfo.isLoading])

  const hasError = useMemo(() => {
    return basicInfo.error || tvlInfo.error || userInfo.error
  }, [basicInfo.error, tvlInfo.error, userInfo.error])

  const data = useMemo(() => {
    if (!basicInfo.data?.shareToken) return undefined

    return {
      ...basicInfo.data,
      totalShares: tvlInfo.totalShares,
      totalAssets: tvlInfo.totalAssets,
      totalAssetsUSD: tvlInfo.value,
      shareToken: basicInfo.data.shareToken,
      baultShareToken: basicInfo.data.shareToken,
      exitFee: `${basicInfo.data.exitFeePercent}%`,
      userShares: userInfo.data?.userShares,
      estimatedLpTokens: userInfo.data?.estimatedLpTokens,
      estimatedLpTokensUSD: userInfo.data?.estimatedLpTokensUSD,
      positions: userInfo.data?.positions || [],
      estimatedLpTokensForShares: userInfo.data?.estimatedLpTokensForShares,
    }
  }, [
    basicInfo.data,
    tvlInfo.totalShares,
    tvlInfo.totalAssets,
    tvlInfo.value,
    userInfo.data?.userShares,
    userInfo.data?.estimatedLpTokens,
    userInfo.data?.estimatedLpTokensUSD,
    userInfo.data?.positions,
    userInfo.data?.estimatedLpTokensForShares,
  ])

  return {
    isLoading,
    error: hasError,
    data,
    tvl: tvlInfo.value,
  }
}
